import { useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { Loader2, Send } from 'lucide-react';

import { useGetSandbox } from '@/api/getSandbox';
import { AppTooltip } from '@/components/appTooltip';
import { PermissionCheck } from '@/components/permissionCheck';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Text } from '@/components/ui/typography';
import {
  type PublishSandboxResponse,
  usePublishSandbox,
} from '@/features/sandbox/api/publishSandbox';
import { SandboxPublishSummaryDialog } from '@/features/sandbox/components/sandboxPublishSummaryDialog';
import { useCurrentSandbox } from '@/hooks/useCurrentSandbox';
import { useGame } from '@/hooks/useGame';
import { useNavigationBlocker } from '@/hooks/useNavigationBlocker';
import { usePermissionCheck } from '@/hooks/usePermissionCheck';
import { toaster } from '@/lib/toaster';
import { Permissions } from '@/models/permission';
import { pendingCountParts, pendingPublish } from '@/models/sandbox';
import { useSandboxContext } from '@/providers';

import { prefetchPublishPreview, PublishPreview } from './publishPreview';

// Mounted only while publishing: a router holds one blocker at a time, and the
// pages this button sits on (imports, translations) may hold their own.
const PublishInProgressGuard = () => {
  useNavigationBlocker(
    true,
    'A publish is in progress. Leaving now hides its result. Leave anyway?',
  );
  return null;
};

/**
 * Publish entry for the current Sandbox, rendered at the end of the breadcrumb trail
 * by the `sandbox` route's `action` handle — so it only ever mounts inside
 * SandboxProvider.
 *
 * Lives in the app layer: its preview reads every publishable feature. The
 * preview renders in this component's own dialog, not `showPopup`'s, whose
 * provider sits above the router and SandboxProvider the queries need.
 */
export const SandboxPublishButton = () => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [publishResult, setPublishResult] =
    useState<PublishSandboxResponse | null>(null);

  const { studioCode, gameCode, selectedSandbox } = useSandboxContext();
  const queryClient = useQueryClient();
  const prefetchPreview = () => {
    prefetchPublishPreview(queryClient, {
      studioCode,
      gameCode,
      sandboxCode: selectedSandbox,
    });
  };
  const game = useGame();

  const { current: currentSandbox } = useCurrentSandbox(
    game.game_code,
    selectedSandbox,
  );

  // GET /v1/sandbox/{id} is gated on sandboxes:read (admin-api router.go), and this
  // button renders on every Sandbox-scoped page — so without the code the query
  // would 403 on every navigation.
  //
  // Known limitation: a Grant carrying sandboxes:publish without sandboxes:read
  // can't reach publish_status at all (the endpoint demands read), so the button
  // stays disabled on "No pending changes" even when there are some. Fixing it
  // needs publish_status on the auth-only sandbox list, not a wider gate here.
  const canReadSandbox = usePermissionCheck({
    oneOf: [Permissions.sandboxes.read],
  });

  const { data: sandboxDetail } = useGetSandbox(
    { sandboxId: currentSandbox?.id ?? '' },
    { enabled: !!currentSandbox?.id && canReadSandbox },
  );

  const pending = pendingPublish(sandboxDetail);

  const publishMutation = usePublishSandbox();

  const handlePublish = async () => {
    if (!currentSandbox) return;
    try {
      setPublishResult(
        await publishMutation.mutateAsync({ sandboxId: currentSandbox.id }),
      );
      setConfirmOpen(false);
    } catch {
      // publishSandbox already toasts the outcome; the dialog stays for a retry.
    }
  };

  return (
    <PermissionCheck oneOf={[Permissions.sandboxes.publish]}>
      {publishMutation.isPending && <PublishInProgressGuard />}
      <AppTooltip
        title={
          pending
            ? `Ready to publish: ${pendingCountParts(pending).join(', ')}`
            : 'No pending changes'
        }
      >
        {/* A disabled button fires no pointer events, so the tooltip needs its own
            trigger — and "nothing to publish" is exactly when it is worth reading. */}
        <span>
          <Button
            size="sm"
            variant={pending ? 'default' : 'ghost'}
            disabled={!pending || publishMutation.isPending}
            onPointerEnter={prefetchPreview}
            onFocus={prefetchPreview}
            onClick={() => {
              if (currentSandbox) setConfirmOpen(true);
              else toaster.info('No sandbox selected');
            }}
          >
            {publishMutation.isPending ? (
              <Loader2 className="animate-spin" />
            ) : (
              <Send />
            )}
            Publish
          </Button>
        </span>
      </AppTooltip>
      <Dialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!publishMutation.isPending) setConfirmOpen(open);
        }}
      >
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>Publish sandbox</DialogTitle>
            <DialogDescription>
              Everything below goes live in{' '}
              <strong className="font-medium text-foreground">
                {currentSandbox?.name}
              </strong>
              . Ready content becomes Published.
            </DialogDescription>
          </DialogHeader>
          <div className="-mx-6 flex max-h-[65vh] min-w-0 flex-col gap-4 overflow-y-auto px-6">
            <PublishPreview />
            {/* Every panel renders nothing when empty, so this is first only then. */}
            <Text variant="muted" className="hidden first:block">
              Nothing to publish.
            </Text>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={publishMutation.isPending}
              onClick={() => {
                setConfirmOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              disabled={publishMutation.isPending}
              onClick={() => {
                void handlePublish();
              }}
            >
              {publishMutation.isPending ? (
                <Loader2 className="animate-spin" />
              ) : (
                <Send />
              )}
              Publish
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {publishResult && (
        <SandboxPublishSummaryDialog
          open={!!publishResult}
          onOpenChange={(open) => {
            if (!open) setPublishResult(null);
          }}
          response={publishResult}
        />
      )}
    </PermissionCheck>
  );
};
